/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { NavBar } from "../../common/NavBar";
import { TotalCamerasTable } from "./TotalCamerasTable";
import { DataUsageGraph } from "./DataUsageGraph";
import { FalsePositiveReductionGraph } from "./FalsePositiveReductionGraph";
import { useNavigate } from "react-router-dom";
import BackdropLoadingScreen from "../../common/BackdropLoadingScreen";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { ReleaseNotesBanner } from "../ReleaseNotesBanner";
import { useDashboardStore } from "./store/dashboardStore";
import { authStateManager } from "../../utils/authStateManager";

export const Dashboard = ({
  navBarCollapsed,
  setNavBarCollapsed,
}: {
  navBarCollapsed: boolean;
  setNavBarCollapsed: Dispatcher<boolean>;
}) => {
  const userName = localStorage.username;
  const firstName = localStorage.first_name;
  const navigate = useNavigate();

  const {
    pageLoading,
    falsePositiveData,
    getTotalCamerasInfo,
    getFalsePositiveData,
    getDataUsageData,
    resetState,
  } = useDashboardStore();

  // Debug functions available globally for console testing (development only)
  React.useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      (window as any).forceTokenExpiration = () => {
        console.log("🧪 DEBUG: Forcing token expiration for testing");
        authStateManager.clearAuthState();
        localStorage.removeItem("loggedIn");
        localStorage.removeItem("token");
        localStorage.removeItem("token_secret");
        window.location.reload();
      };

      (window as any).simulateExpiredToken = () => {
        console.log(
          "🧪 DEBUG: Simulating expired token (keeping tokens but marking as invalid)"
        );
        localStorage.removeItem("loggedIn");
        window.location.reload();
      };

      (window as any).simulateTrulyExpiredToken = () => {
        console.log(
          "🧪 DEBUG: Simulating truly expired token (invalid access token but valid refresh token)"
        );

        // Get the current refresh token (keep it valid)
        const currentRefreshToken = document.cookie
          .split("; ")
          .find((row) => row.startsWith("token_secret="))
          ?.split("=")[1];

        if (!currentRefreshToken) {
          console.log(
            "🧪 DEBUG: No refresh token found, cannot simulate this scenario"
          );
          return;
        }

        // Clear the access token cookie (make it expired/invalid)
        document.cookie =
          "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

        // Keep the refresh token valid (don't touch it)
        console.log("🧪 DEBUG: Access token cleared, refresh token kept valid");

        // Reload to trigger the scenario
        window.location.reload();
      };
    }
  }, []);

  React.useEffect(() => {
    // Reset state when component mounts
    resetState();
  }, []);

  React.useEffect(() => {
    // Fetch initial data
    getTotalCamerasInfo(navigate);
  }, []);

  React.useEffect(() => {
    if (!falsePositiveData) {
      getFalsePositiveData(navigate);
    }
  }, [falsePositiveData]);

  React.useEffect(() => {
    getDataUsageData(navigate);
  }, []);

  return (
    <div>
      <ReleaseNotesBanner />
      <BackdropLoadingScreen openState={pageLoading} />
      <div className="bg-actuate-grey absolute w-full h-full overflow-y-auto overflow-x-hidden">
        <div
          className={
            navBarCollapsed ? "pl-[95px] pr-6 pb-10" : "pl-[170px] pr-6 pb-10"
          }
        >
          <div className="pt-2 pb-2 border-b-4 border-[#BDBDBD]">
            {!firstName ? (
              <p className="text-actuate-blue font-mulish text-[20px] font-bold">
                Welcome, {userName}
              </p>
            ) : (
              <p className="text-actuate-blue font-mulish text-[20px] font-bold">
                Welcome, {firstName}
              </p>
            )}
          </div>
          <div className="mt-5 grid grid-cols-1 gap-6 xl:grid-cols-2 items-start">
            <TotalCamerasTable />
            <div className="flex min-w-0 flex-col gap-6">
              <FalsePositiveReductionGraph />
              <DataUsageGraph />
            </div>
          </div>
        </div>
      </div>
      <div className="justify-start">
        <NavBar
          navBarCollapsed={navBarCollapsed}
          setNavBarCollapsed={setNavBarCollapsed}
          props={{}}
        />
      </div>
    </div>
  );
};
